import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import { getConfig } from "@/services/api/config";
import { getClerkToken } from "@/services/clerk";
import type {
  AdminMemberDetail,
  AdminMembersPage,
  MemberProfile,
} from "@/features/members/members.types";

export type AdminStats = {
  members: {
    total: number;
    active: number;
    /** Paying members only — gifts are counted separately, never as revenue. */
    paid: number;
    /** Full access, granted on request, nobody charged. */
    gift: number;
    /** Preview membership. */
    free: number;
    pending_payment: number;
    lapsed: number;
  };
  queues: {
    pending_applications: number;
    /** Optional: older deployments do not send it. */
    pending_organizations?: number;
    pending_resources: number;
    /** The in-app board went out with Moderation on 25 August, so this counts
     *  a queue that cannot grow. Still sent, no longer shown. */
    pending_discussions: number;
  };
  content: {
    published_resources: number;
    published_threads: number;
    emails_sent: number;
    emails_queued: number;
    emails_suppressed: number;
  };
  top_contributors: { member_id: number; name: string; count: number }[];
  active_threads: {
    id: number;
    title: string;
    reply_count: number;
    last_activity_at: string;
  }[];
};

export type ActivityReport = {
  totals: Record<string, number>;
  payments: Record<string, number>;
  top_profiles: { target_id: string; label: string; count: number }[];
  top_threads: { target_id: string; label: string; count: number }[];
  top_resources: { target_id: string; label: string; count: number }[];
  most_active_members: { member_id: number; name: string; count: number }[];
  recent: {
    id: number;
    created_at: string;
    type: string;
    member_name: string;
    target_label: string;
    meta: unknown;
  }[];
  truncated: boolean;
};


export type AdminSettings = {
  applicationsOpen: boolean;
  applicationsClosedMessage: string;
  autoApproveApplications: boolean;
  notifyAdminsOnApplication: boolean;
  emailsEnabled: boolean;
  emailFrom: string | null;
  emailReplyTo: string | null;
  supportEmail: string | null;
  requireModeration: boolean;
  emailFromEffective: string;
  smtpConfigured: boolean;
  /** Whether a payment can be taken at all. Every Stripe id is mode-specific,
   *  so an environment with no key cannot charge anyone and the pricing card
   *  says so rather than presenting figures that mean nothing yet. */
  stripeConfigured: boolean;
  pricing: {
    processingFeeCents: number;
    variants: Record<
      string,
      {
        enabled: boolean;
        membershipCents?: number;
        suggestedCents?: number;
        minimumCents?: number;
        stripePriceId?: string | null;
      }
    >;
  };
};

/** The roles an administrator can give, in the order the dropdown lists them. */
export const MEMBER_ROLES = [
  { value: "member", label: "Member" },
  { value: "moderator", label: "Moderator" },
  { value: "admin", label: "Admin" },
];

export const useAdminStats = () =>
  useQuery<AdminStats>({
    queryKey: ["afell", "admin", "stats"],
    queryFn: () => apiRequest("/afell/admin/stats/"),
  });

export const useActivityReport = (days: number) =>
  useQuery<ActivityReport>({
    queryKey: ["afell", "admin", "activity", days],
    queryFn: () => apiRequest(`/afell/admin/activity/?days=${days}`),
  });


export const useAdminMembers = (
  search: string,
  page = 1,
  sort: string | null = null,
  direction: "asc" | "desc" = "asc"
) =>
  useQuery<AdminMembersPage>({
    queryKey: ["afell", "admin", "members", search, page, sort, direction],
    queryFn: () =>
      apiRequest(
        `/afell/admin/members/?page=${page}` +
          (search ? `&search=${encodeURIComponent(search)}` : "") +
          // Sorted server-side: the page is a window onto 470 people, so
          // ordering the fifty already fetched would sort the wrong set.
          (sort ? `&sort=${sort}&direction=${direction}` : "")
      ),
    placeholderData: keepPreviousData,
  });

/** One member, for the manage page. Under the list's key so everything that
 *  refreshes the table refreshes this too. */
export const useAdminMember = (id: string | undefined) =>
  useQuery<AdminMemberDetail>({
    queryKey: ["afell", "admin", "members", "one", id],
    queryFn: () => apiRequest(`/afell/admin/members/${id}/`),
    enabled: Boolean(id),
  });

export function useAdminMemberUpdate() {
  const queryClient = useQueryClient();
  return useMutation<
    MemberProfile,
    Error,
    { id: number } & Record<string, unknown>
  >({
    mutationFn: ({ id, ...payload }) =>
      apiRequest(`/afell/admin/members/${id}/`, "POST", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "audit"] });
      // An admin can edit their own row (the notification switch on Settings
      // does exactly that), and `me` is where the page reads it back.
      queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
    },
  });
}

export const useAdminSettings = () =>
  useQuery<AdminSettings>({
    queryKey: ["afell", "admin", "settings"],
    queryFn: () => apiRequest("/afell/admin/settings/"),
  });

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation<{ changes: string[] }, Error, Record<string, unknown>>({
    mutationFn: (payload) => apiRequest("/afell/admin/settings/", "POST", payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell"] }),
  });
}

export function usePricingAction(path: "variant-price" | "donation-price" | "processing-fee") {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, Record<string, unknown>>({
    mutationFn: (payload) => apiRequest(`/afell/admin/settings/${path}/`, "POST", payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "settings"] }),
  });
}

/** Record that an admin put this member on the Google Group, or took them off.
 *  The group has no API, so this is a note of a hand action. */
/** The member list as a CSV, for Soft Network's mail-outs.
 *
 * Their subscriber list is on Squarespace and stopped being fed when
 * membership moved here, so rather than keep a second copy of the membership
 * anywhere, they take the current one whenever they need it. A fetch rather
 * than a link, because the download carries the Clerk token. */
export async function downloadMembersCsv(search: string) {
  const headers: Record<string, string> = {};
  const token = await getClerkToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  const response = await fetch(`${getConfig().apiUrl}/afell/admin/members/export/${query}`, {
    headers,
    credentials: "include",
  });
  if (!response.ok) throw new Error("The export could not be downloaded");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = "afell-members.csv";
  link.click();
  URL.revokeObjectURL(url);
}

/** Take a member out of the directory, or put them back. Nothing else about
 *  them changes; see AdminService.set_member_listed. */
export function useSetMemberListed() {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; listed: boolean }, Error, { id: number; listed: boolean }>({
    mutationFn: ({ id, listed }) =>
      apiRequest(`/afell/admin/members/${id}/listed/`, "POST", { listed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
    },
  });
}

/** Move a member who has never signed in to another address, and optionally
 *  send the invitation there; see AdminService.change_sign_in_email. */
export function useChangeSignInEmail() {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; email: string; invited: boolean },
    Error,
    { id: number; email: string; invite: boolean }
  >({
    mutationFn: ({ id, ...body }) =>
      apiRequest(`/afell/admin/members/${id}/email/`, "POST", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "member"] });
    },
  });
}

/** The invitation again, to the address already on file, for a member who
 *  never signed in and whose mail was missed. */
export function useResendInvitation() {
  return useMutation<{ id: number; email: string }, Error, { id: number }>({
    mutationFn: ({ id }) =>
      apiRequest(`/afell/admin/members/${id}/invitation/`, "POST", {}),
  });
}

/** One organization a member stewards, and what it is doing for their
 *  membership. `funded_by_others` leaves their own payment out. */
export type MemberCoverageRow = {
  organization_id: number;
  organization: string;
  funded_by_others: boolean;
  claim_id: number | null;
  status: "requested" | "approved" | "declined" | "revoked" | null;
  note: string;
  covering: boolean;
};

export const useMemberCoverage = (id: number) =>
  useQuery<{ organizations: MemberCoverageRow[] }>({
    queryKey: ["afell", "admin", "members", "coverage", id],
    queryFn: () => apiRequest(`/afell/admin/members/${id}/coverage/`),
  });

/** Cover a member through an organization, approve or decline their own
 *  request to be, or take coverage back. One mutation, because the page
 *  refreshes the same things after each. */
export function useCoverageAction(memberId: number) {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    | { action: "grant"; organization_id: number }
    | { action: "approved" | "declined" | "revoke"; claim_id: number }
  >({
    mutationFn: (body) =>
      body.action === "grant"
        ? apiRequest(`/afell/admin/members/${memberId}/coverage/`, "POST", {
            organization_id: body.organization_id,
          })
        : body.action === "revoke"
          ? apiRequest(`/afell/admin/org-claims/${body.claim_id}/revoke/`, "POST", {})
          : apiRequest(`/afell/admin/org-claims/${body.claim_id}/review/`, "POST", {
              decision: body.action,
            }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] }),
  });
}

/** What a member pays for and the last thing they were charged, from Stripe. */
export type MemberBilling = {
  hasStripeCustomer: boolean;
  subscriptions: {
    id: string;
    kind: string;
    status: string;
    quantity: number;
    amountCents: number;
    interval: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  }[];
  lastCharge: {
    id: string;
    amountCents: number;
    refundedCents: number;
    created: string | null;
    description: string;
  } | null;
  payments: { id: number; created_at: string; kind: string; amount_cents: number }[];
};

export const useMemberBilling = (id: number) =>
  useQuery<MemberBilling>({
    queryKey: ["afell", "admin", "members", "billing", id],
    queryFn: () => apiRequest(`/afell/admin/members/${id}/billing/`),
  });

/** Stop, resume or refund on a member's behalf. Stripe's webhook is what
 *  changes the membership after a cancellation, so the member's row is
 *  refreshed a second time once it has had a moment to arrive. */
export function useBillingAction(memberId: number) {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
  return useMutation<
    unknown,
    Error,
    | { action: "period_end" | "now" | "resume"; subscription_id: string }
    | { action: "refund"; charge_id: string }
  >({
    mutationFn: (body) =>
      body.action === "refund"
        ? apiRequest(`/afell/admin/members/${memberId}/refund/`, "POST", {
            charge_id: body.charge_id,
          })
        : apiRequest(
            `/afell/admin/members/${memberId}/subscriptions/${body.subscription_id}/cancel/`,
            "POST",
            { when: body.action }
          ),
    onSuccess: () => {
      refresh();
      setTimeout(refresh, 4000);
    },
  });
}

/** The Google Group's member export set beside the site's members. */
export type ListservComparison = {
  onGroup: number;
  unknown: {
    email: string;
    possible: { id: number; name: string; why: "same domain" | "surname" }[];
  }[];
  missing: { id: number; name: string; address: string; recorded: boolean }[];
  foundUnrecorded: number;
  recorded: number;
};

export function useCompareListserv() {
  const queryClient = useQueryClient();
  return useMutation<ListservComparison, Error, { export: string; record?: boolean }>({
    mutationFn: (body) => apiRequest("/afell/admin/listserv/compare/", "POST", body),
    onSuccess: (_data, body) => {
      if (body.record)
        queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] });
    },
  });
}

/** Where a member stands with the Google Group: on it, opted out, or nobody
 *  has got to them yet. Three states, because "not added" was answering two
 *  questions and only one of them is work to do (Sami, 2026-09-22). */
export type ListservState = "added" | "opted_out" | "outstanding";

export function useSetListservAdded() {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; listserv_added_at: string | null; listserv_opted_out_at: string | null },
    Error,
    { id: number; state: ListservState }
  >({
    mutationFn: ({ id, state }) =>
      apiRequest(`/afell/admin/members/${id}/listserv/`, "POST", { state }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["afell", "admin", "members"] }),
  });
}
